import _ from 'lodash'
import { EntityState } from '@reduxjs/toolkit'
import { CommonState, Model } from '@/core/classes/Model'
import { InviteParticipantRequestParams, JoinMeetingRequestParams, SearchRequest, SearchRequestOptions } from '@/core/types/request'
import { DictaphoneDocument, Document } from './document.model'
import { Establishment } from './establishment.model'
import { Patient } from './patient.model'
import { Staff } from './staff.model'
import { FormSubmission } from './form.model'
import { StaffType } from '@/utils/onboarding/type'
import { PatientConsent } from './meeting-link-order.model'

export enum ConsultationParticipantType {
  DOCTOR = 'DOCTOR',
  PARENT = 'PARENT',
  PATIENT = 'PATIENT',
  PDS = 'PDS',
}

export enum ConsultationType {
  Classic = 'classic',
  Emergency = 'emergency',
  Local = 'local',
  MeetingOnly = 'meeting_only',
  Netsoins = 'netsoins',
  Pedcl = 'pedcl',
}

export enum ConsultationStatus {
  Scheduled = 'scheduled',
  Accepted = 'accepted',
  Refused = 'refused',
  Active = 'active',
  Canceled = 'canceled',
  Completed = 'completed',
}

export enum ConsultationReadinessStatus {
  Preparing = 'preparing',
  Ready = 'ready',
}

export type ConsultationReadiness = {
  status: ConsultationReadinessStatus
  readyAt?: string
  readyBy?: string
  note?: string
}

export enum PaymentType {
  Card = 'CARD',
  Cash = 'CASH',
  Check = 'CHECK',
  Transfer = 'TRANSFER',
}

export type Act = {
  date: string
  code: string
  price: number
}

export type ICalendso = {
  email: string
  password: string
  user: string
  dateFrom: string
  dateTo: string
  end: string
  eventTypeId: number
  eventTypeSlug: string
  guests: []
  language: string
  name: string
  notes: string
  start: string
  timeZone: string
}

export type addBiologicalDto = {
  label: string
  comment: string
  unit: string
  value: string
  file?: string
  files: string[]
}

export type Biological = {
  label: string
  comment: string
  unit: string
  value: string
  file?: string
  files: string[]
  id: string
}

type ConsultationParticipant = {
  email: string
  isGuest?: boolean
  name: string
  surname: string
  type: ConsultationParticipantType
}

export type ConsultationReason = {
  _id: string
  name: string
}

export type CreateConsultationReasonDto = {
  name: string
}

export type UpdateConsultationReasonDto = Partial<CreateConsultationReasonDto>

export type DesmosActStatus = {
  success: boolean
  message?: string
  createdAt?: string
  acts: Array<{
    libelle: string
    success: boolean
    error?: string
    errorCode?: string
    role?: StaffType
    actId?: string
    establishmentId?: string
  }>
}

export enum BillingReviewStatus {
  Pending = 'pending',
  Completed = 'completed',
  Issue = 'issue',
}

export enum BillingIssueReason {
  DesmosDown = 'desmos_down',
  NoSocialSecurity = 'no_social_security',
  PatientRefused = 'patient_refused',
  TechnicalIssue = 'technical_issue',
  MissingInfo = 'missing_info',
  Other = 'other',
}

export type BillingReview = {
  status: BillingReviewStatus
  issueReason?: BillingIssueReason
  note?: string
  reviewedBy?: string
  reviewedAt?: string
  justificationFileKeys?: string[]
}

export type UpdateBillingReviewDto = {
  status: BillingReviewStatus
  issueReason?: BillingIssueReason
  note?: string
  justificationFileKeys?: string[]
}

export type Consultation = {
  _id: string
  createdBy: Staff | string
  commentaire?: string
  chatRoom?: string
  endDate?: string
  establishment?: string | Establishment
  files: Document[]
  mesures: Array<{ label: string; value: string | number | null; unit: string; order: number }>
  biologicals: Array<Biological>
  paid?: boolean
  price?: number
  paymentType?: PaymentType
  validatedAt?: string
  receiptUrl?: string
  participants?: ConsultationParticipant[]
  patient: Patient
  pds?: Staff
  medecin?: Staff
  notification?: number
  startDate: string
  status: ConsultationStatus
  transcription?: string
  type: ConsultationType
  waitingRoom?: string
  waitingRoomJoinedAt?: string
  dictaphones: DictaphoneDocument[]
  reason?: string
  speciality?: string
  autoRecording?: boolean
  recordingConsentPending?: boolean
  patientConsent?: PatientConsent
  professionalConsent?: {
    dataReuseConsent?: boolean
    preConsultationConsent?: boolean
    consentDate?: string
    teleconsultationUnderstandingAssessed?: boolean
    teleconsultationUnderstandingAssessedAt?: string
    teleconsultationUnderstandingAssessmentVersion?: string
    version?: string
  }
  formSubmissions?: FormSubmission[]
  report?: Report
  desmosActStatus?: DesmosActStatus
  billingReview?: BillingReview
  groupId?: string
  patientLocation?: {
    description: string
    placeId?: string
  }
  readiness?: ConsultationReadiness
}

export type CreateConsultationDto = {
  commentaire?: string
  medecin?: string | null
  network?: string
  patient?: string
  patients?: string[]
  pds?: string
  startDate: string
  expectedDuration?: number
  notification?: number
  price?: number
  type?: Consultation['type']
  status?: string
  speciality?: string
  waitingRoom?: string
  reason?: string
  transcription?: string
  paymentType?: PaymentType
  autoRecording?: boolean
  groupId?: string
}

export type UpdateConsultationDto = Partial<CreateConsultationDto> & {
  status?: Consultation['status']
  participants?: Consultation['participants']
  professionalConsent?: {
    dataReuseConsent?: boolean
    preConsultationConsent?: boolean
    consentDate?: string
    teleconsultationUnderstandingAssessed?: boolean
    teleconsultationUnderstandingAssessedAt?: string
    teleconsultationUnderstandingAssessmentVersion?: string
    version?: string
  }
}

export type SearchedConsultationParticipant = Omit<ConsultationParticipant, 'isGuest'> & {
  name: string
  surname: string
}

export type TwilioStunServer = {
  accountSid: string
  dateCreated: string
  dateUpdated: string
  iceServers: any[]
  password: string
  ttl: string
  username: string
}

export type ConsultationState = CommonState & {
  consultationReasons: EntityState<ConsultationReason, ConsultationReason['_id']>
  consultations: EntityState<Consultation, Consultation['_id']>
  chimeMeetings: Record<
    string,
    {
      attendeeInfo: Record<string, any> | null
      meetingInfo: Record<string, any> | null
    }
  >
  participantsByConsultationId: Record<string, Staff[]>
  rpiMeetingId: string | null
}

export type ConsultationDeviceUsage = {
  deviceType: 'stethoscope' | 'medical-camera'
  customName?: string
  deviceLabel?: string
  trigger: 'auto' | 'manual'
}

class ConsultationModel extends Model {
  static instance: ConsultationModel

  static getInstance() {
    if (!ConsultationModel.instance) {
      ConsultationModel.instance = new ConsultationModel('consultation')
    }
    return ConsultationModel.instance
  }

  getInitialState(): ConsultationState {
    return _.merge({}, this.commonState, {
      requestTracker: {
        joinMeeting: {
          loading: false,
          error: null,
        },
        invite: {
          loading: false,
          error: null,
        },
      },
      consultationReasons: { entities: {}, ids: [] },
      consultations: { entities: {}, ids: [] },
      chimeMeetings: {
        visioConference: { attendeeInfo: null, meetingInfo: null },
        medicalVideo: { attendeeInfo: null, meetingInfo: null },
        stethoscope: { attendeeInfo: null, meetingInfo: null },
      },
      participantsByConsultationId: {},
      rpiMeetingId: null,
    })
  }

  bookMeeting = async (meeting: Partial<ICalendso>): Promise<any> => {
    return await this.httpClient.request<any>('/calendso-p/bookEvent', 'POST', meeting)
  }

  createConsultation = async (createConsultationDto: CreateConsultationDto): Promise<Consultation> => {
    return await this.httpClient.request<Consultation>('/consultations', 'POST', createConsultationDto)
  }

  createConsultationReason = async (createConsultationReasonDto: CreateConsultationReasonDto) => {
    return await this.httpClient.request<ConsultationReason>('/consultations/reasons', 'POST', createConsultationReasonDto)
  }

  deleteConsultationReason = async (consultationReasonId: string) => {
    return await this.httpClient.request<ConsultationReason>(`/consultations/reasons/${consultationReasonId}`, 'DELETE')
  }

  endConsultation = async (consultationId: string): Promise<Consultation> => {
    return await this.httpClient.request<Consultation>(`/consultations/${consultationId}/end`, 'PATCH')
  }

  getConsultationReasons = async (params?: SearchRequest) => {
    return await this.httpClient.request<ConsultationReason[]>('/consultations/reasons', 'GET', params)
  }

  getConsultation = async (id: string): Promise<Consultation> => {
    return await this.httpClient.request<Consultation>(`/consultations/${id}`, 'GET')
  }

  getConsultations = async (query: any, options?: SearchRequestOptions): Promise<[Consultation[], number]> => {
    // TODO: working with back to not spread props of options
    return await this.httpClient.request<[Consultation[], number]>('/consultations/query', 'POST', { query, ...options })
  }

  getRpiMeetingId = async (): Promise<string | undefined> => {
    const { data: rpiMeetingId } = await this.httpClient.axios.get<string | undefined>(import.meta.env.VITE_RPI_SOCKET_WRAPPER_URL!)
    return rpiMeetingId
  }

  getStunServer = async (): Promise<TwilioStunServer> => {
    let emptyTwilioStunServer: TwilioStunServer = {
      accountSid: '',
      dateCreated: '',
      dateUpdated: '',
      iceServers: [],
      password: '',
      ttl: '',
      username: '',
    }
    try {
      let twilioServer = await this.httpClient.request<TwilioStunServer>('/consultations/other/stun', 'GET')
      return twilioServer
    } catch (exception) {
      console.warn(exception)
      return emptyTwilioStunServer
    }
  }

  joinMeeting = async (params: JoinMeetingRequestParams) => {
    return await this.httpClient.request('/rooms/meetings/join', 'POST', params)
  }

  updateConsultation = async (id: string, changes: UpdateConsultationDto): Promise<Consultation> => {
    return await this.httpClient.request<Consultation>(`/consultations/${id}`, 'PATCH', changes)
  }

  updateConsultationReason = async (consultationReasonId: string, updateConsultationReasonDto: UpdateConsultationReasonDto) => {
    return await this.httpClient.request<ConsultationReason>(`/consultations/reasons/${consultationReasonId}`, 'PATCH', updateConsultationReasonDto)
  }

  sendMesures = async (id: string, measures: Consultation['mesures']): Promise<Consultation> => {
    return await this.httpClient.request<Consultation>(`/consultations/${id}/measures`, 'POST', { measures })
  }

  /**
   * Reports the activation of an exam device (stethoscope, medical camera) during a
   * consultation so device usage is traceable even without an archived capture.
   * Fire-and-forget: deduplicated per session and never throws.
   */
  reportDeviceUsage = async (consultationId: string, deviceUsage: ConsultationDeviceUsage): Promise<void> => {
    const dedupeKey = [consultationId, deviceUsage.deviceType, deviceUsage.customName ?? '', deviceUsage.deviceLabel ?? '', deviceUsage.trigger].join('|')
    if (this.reportedDeviceUsages.has(dedupeKey)) return
    this.reportedDeviceUsages.add(dedupeKey)
    try {
      await this.httpClient.request<Consultation>(`/consultations/${consultationId}/device-usage`, 'POST', { ...deviceUsage })
    } catch {
      this.reportedDeviceUsages.delete(dedupeKey)
    }
  }

  private reportedDeviceUsages = new Set<string>()

  sendBiological = async (consultationId: string, biological: Biological): Promise<Consultation> => {
    return await this.httpClient.request<Consultation>(`/consultations/${consultationId}/biologicals`, 'POST', { ...biological })
  }

  deleteBiological = async (consultationId: string, biologicalId: string): Promise<Consultation> => {
    return await this.httpClient.request<Consultation>(`/consultations/${consultationId}/biologicals/${biologicalId}`, 'DELETE')
  }

  saveMesures = async (consultationId: string, csv: string): Promise<Consultation> => {
    return await this.httpClient.request<Consultation>(`/files/consultation/${consultationId}/mesures/csv`, 'POST', { csv })
  }

  sendInvite = async (consultationId: string, participant: InviteParticipantRequestParams): Promise<Consultation> => {
    // Callers pass search results carrying name/surname; the API rejects unknown fields (forbidNonWhitelisted)
    const body: InviteParticipantRequestParams = _.pick(participant, ['email', 'type', 'assignToDoctor', 'skipNotification'])
    return await this.httpClient.request<Consultation>(`/consultations/${consultationId}/invite`, 'POST', body)
  }

  createConsultationChannel = async (consultationId: string, room_name: string) => {
    return await this.httpClient.request(`/rocketchat/channel`, 'POST', { consultationId, room_name })
  }

  getFSP = async (id: string, acts: Act[]): Promise<Consultation> => {
    return await this.httpClient.request<Consultation>(`/consultations/${id}/fsp`, 'POST', { acts })
  }

  searchParticipants = async (searchTerm: string, participantType: string, consultationId: string) => {
    return await this.httpClient.request<SearchedConsultationParticipant[]>(`/consultations/${consultationId}/participants/search`, 'GET', {
      participant_type: participantType,
      search_term: searchTerm,
    })
  }
}

export default ConsultationModel.getInstance()
